// ── BCH Rooms — native audio rooms on NIP-53 ──────────────────────
//
// Rooms are hosted by the app itself over peer-to-peer WebRTC; there is
// no third-party audio service. Everything a room needs rides on
// standard NIP-53 kinds so other Nostr clients can see our rooms:
//
//   • kind 30312 — the room itself (addressable "meeting space")
//   • kind 10312 — per-user presence, replaceable, carries the raised
//     hand flag. This is what makes the roster honest (see below).
//   • kind 1311  — live chat, reusing components/live/LiveChatPanel
//   • kind 25050 — WebRTC signalling (app-specific, see NIP.md)
//
// ── Why presence is a separate event ─────────────────────────────
//
// In a serverless mesh there is no authority holding the participant
// list. If the roster were built only from WebRTC `join`/`leave`
// signals it would fill with ghosts: `leave` never fires when a laptop
// closes, a tab is killed, or a phone loses signal. NIP-53 solves this
// with kind 10312 — a *replaceable* presence event each participant
// re-publishes on a timer. A stale presence event simply ages out, so
// absence is inferred from silence rather than from a farewell message
// that may never arrive.

import type { Event } from 'nostr-tools';

// ── Kinds ─────────────────────────────────────────────────────────

/** NIP-53 meeting space (addressable). The room definition. */
export const ROOM_KIND = 30312;
/** NIP-53 room presence (replaceable). Heartbeat + raised hand. */
export const ROOM_PRESENCE_KIND = 10312;
/** App-specific WebRTC signalling envelope. See NIP.md. */
export const ROOM_SIGNAL_KIND = 25050;

/**
 * How often each participant re-publishes presence.
 *
 * NIP-53 says presence "SHOULD be updated at regular intervals" without
 * naming one. 30s is a compromise: frequent enough that a dropped user
 * disappears quickly, sparse enough to avoid hammering relays (and, for
 * NIP-07/NIP-46 users, the signer — every heartbeat is a signature).
 */
export const PRESENCE_INTERVAL_MS = 30_000;

/**
 * Presence older than this counts as gone. Must be comfortably more than
 * one interval so a single dropped relay message doesn't evict someone
 * who is still connected; 2.5 intervals tolerates one miss.
 */
export const PRESENCE_TIMEOUT_MS = 75_000;

/** A room with no presence newer than this is not "live". */
export const ROOM_LIVE_WINDOW_SECS = 300;

// ── ICE / TURN configuration ──────────────────────────────────────
//
// STUN alone only works when at least one side can accept an inbound
// connection. Behind symmetric NAT, CGNAT (most mobile carriers) or a
// corporate firewall, the handshake fails and the user just sees a room
// that never connects. The only fix is TURN, which relays the audio —
// and that is a real server with real bandwidth cost.
//
// No TURN server is configured yet, so this reads from localStorage:
// operators can point rooms at a TURN server without a redeploy, and
// the transport never needs to change. When a TURN service is chosen,
// bake the default in here.

const TURN_CONFIG_KEY = 'bchnostr:turn-config';

/**
 * NIP-78 `d` tag the operator publishes the shared TURN config under.
 *
 * Distributing the relay config over Nostr rather than hard-coding it in
 * the bundle means credentials can be **rotated without a redeploy** —
 * important because a static credential sits in every user's browser and
 * a provider's free tier can be exhausted or abused.
 *
 * TRUST BOUNDARY: this event is only ever honoured from `OPERATOR_PUBKEY`.
 * Anyone can publish kind 30078 with this `d` tag, and an attacker who
 * could substitute their own relay would see every participant's traffic.
 * Always filter by author — never trust the `d` tag alone.
 */
export const TURN_CONFIG_D_TAG = 'bchnostr/turn-config';

/** Cache key for the operator-published config, so rooms work offline-ish. */
const TURN_SHARED_CACHE_KEY = 'bchnostr:turn-config-shared';

/**
 * Build the ICE server list for a provider that issues **static long-term
 * credentials** (ExpressTURN, coturn with `lt-cred-mech`, …).
 *
 * Static credentials can live in the client, which is the whole appeal:
 * no credential endpoint, no backend, nothing to deploy. Providers that
 * issue short-lived credentials from a secret (Cloudflare, coturn with
 * `use-auth-secret`) cannot work this way — their secret must never reach
 * the browser — so they need a server to mint per-user credentials.
 *
 * The trade-off of static credentials is that anyone reading the JS bundle
 * can reuse them. That is an accepted cost here: the credential only buys
 * relay bandwidth on a metered free tier, never access to user data, and
 * it can be rotated from the provider dashboard at any time.
 *
 * `host` is the provider's relay hostname, e.g. `relay1.expressturn.com`.
 * All of UDP / TCP / TLS-443 are included: 443 is what survives networks
 * that block UDP outright, which is exactly when TURN is needed most.
 */
export function buildStaticTurnUrls(host: string): string[] {
  return [
    `turn:${host}:3478?transport=udp`,
    `turn:${host}:3478?transport=tcp`,
    `turns:${host}:443?transport=tcp`,
  ];
}

export interface TurnConfig {
  /**
   * One TURN URL or several. Providers typically return a list covering
   * UDP, TCP and TLS ports — keep them all: `turns:…:443?transport=tcp`
   * is the variant that gets through corporate firewalls that block UDP
   * outright, which is exactly the case TURN exists to rescue.
   */
  urls: string | string[];
  username?: string;
  credential?: string;
  /**
   * Unix seconds when these credentials expire, if they are short-lived.
   * Expired credentials are ignored so we fall back to STUN rather than
   * silently failing every connection.
   */
  expiresAt?: number;
}

const BASE_ICE_SERVERS: RTCIceServer[] = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
  { urls: 'stun:stun.cloudflare.com:3478' },
];

/** Read the operator-supplied TURN server, if any. */
export function getTurnConfig(): TurnConfig | null {
  try {
    const raw = localStorage.getItem(TURN_CONFIG_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as TurnConfig;
    if (!parsed) return null;

    // Only genuine TURN URLs belong here. Anything else would be ignored
    // by the browser anyway; rejecting it makes misconfiguration obvious
    // instead of looking like a mysterious connection failure.
    const urls = (Array.isArray(parsed.urls) ? parsed.urls : [parsed.urls])
      .filter(u => typeof u === 'string' && /^turns?:/i.test(u));
    if (urls.length === 0) return null;

    // Short-lived credentials that have expired are worse than none: every
    // relay attempt fails authentication. Drop them and use plain STUN.
    if (typeof parsed.expiresAt === 'number' &&
        parsed.expiresAt * 1000 < Date.now()) {
      return null;
    }

    return { ...parsed, urls };
  } catch {
    return null;
  }
}

export function setTurnConfig(config: TurnConfig | null) {
  try {
    if (!config) localStorage.removeItem(TURN_CONFIG_KEY);
    else localStorage.setItem(TURN_CONFIG_KEY, JSON.stringify(config));
  } catch { /* storage unavailable */ }
}

// ── Operator-published (shared) TURN config ───────────────────────

/** Validate an untrusted object into a TurnConfig, or null. */
function coerceTurnConfig(raw: unknown): TurnConfig | null {
  if (!raw || typeof raw !== 'object') return null;
  const obj = raw as Record<string, unknown>;

  const rawUrls = Array.isArray(obj.urls) ? obj.urls : [obj.urls];
  const urls = rawUrls.filter(
    (u): u is string => typeof u === 'string' && /^turns?:/i.test(u),
  );
  if (urls.length === 0) return null;

  const expiresAt = typeof obj.expiresAt === 'number' ? obj.expiresAt : undefined;
  if (expiresAt !== undefined && expiresAt * 1000 < Date.now()) return null;

  return {
    urls,
    username: typeof obj.username === 'string' ? obj.username : undefined,
    credential: typeof obj.credential === 'string' ? obj.credential : undefined,
    expiresAt,
  };
}

/**
 * Parse the operator's kind 30078 TURN announcement.
 *
 * The caller MUST have restricted the query to `OPERATOR_PUBKEY`; this
 * function cannot verify authorship on its own.
 */
export function parseSharedTurnConfig(content: string): TurnConfig | null {
  try {
    return coerceTurnConfig(JSON.parse(content));
  } catch {
    return null;
  }
}

/** Persist the operator's config so it survives reloads and slow relays. */
export function cacheSharedTurnConfig(config: TurnConfig | null) {
  try {
    if (!config) localStorage.removeItem(TURN_SHARED_CACHE_KEY);
    else localStorage.setItem(TURN_SHARED_CACHE_KEY, JSON.stringify(config));
  } catch { /* storage unavailable */ }
}

/** The operator-published config as last seen by this browser. */
export function getCachedSharedTurnConfig(): TurnConfig | null {
  try {
    const raw = localStorage.getItem(TURN_SHARED_CACHE_KEY);
    return raw ? coerceTurnConfig(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

/**
 * The TURN config actually used for connections.
 *
 * A locally-set config wins so the operator can test a new relay without
 * disturbing anyone; otherwise the operator-published one is used.
 */
export function getEffectiveTurnConfig(): TurnConfig | null {
  return getTurnConfig() ?? getCachedSharedTurnConfig();
}

/** True when a TURN relay is available, i.e. restrictive NATs can connect. */
export function hasTurn(): boolean {
  return getEffectiveTurnConfig() !== null;
}

/** True when *this browser* has a local override (operator testing). */
export function hasLocalTurnOverride(): boolean {
  return getTurnConfig() !== null;
}

/** ICE servers for a peer connection: STUN always, TURN when configured. */
export function getIceServers(): RTCIceServer[] {
  const turn = getEffectiveTurnConfig();
  if (!turn) return BASE_ICE_SERVERS;
  return [
    ...BASE_ICE_SERVERS,
    { urls: turn.urls, username: turn.username, credential: turn.credential },
  ];
}

// ── Device / browser capability checks ────────────────────────────
//
// Mobile is where audio rooms are actually used, and it is also where the
// platform fights back. These checks let the UI warn *before* someone
// taps "Join with mic" and concludes the app is broken.

/** iOS (including iPadOS reporting itself as a Mac with touch). */
export function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  // iPadOS 13+ masquerades as macOS; touch points give it away.
  return /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
}

/** Running as an installed PWA rather than in a browser tab. */
export function isStandalonePWA(): boolean {
  if (typeof window === 'undefined') return false;
  const iosStandalone = (navigator as Navigator & { standalone?: boolean }).standalone;
  if (iosStandalone === true) return true;
  try {
    return window.matchMedia('(display-mode: standalone)').matches;
  } catch {
    return false;
  }
}

/** Is a microphone capture API present at all? */
export function canCaptureMic(): boolean {
  return typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia;
}

/**
 * A blocking problem that will stop this device from speaking in a room,
 * or null when the device should work.
 *
 * The iOS standalone case is a real WebKit bug (webkit.org bug 185448):
 * `getUserMedia` fails inside an installed PWA even though it works in
 * Safari, and the mic is also cut when the phone locks or the user
 * switches apps. Since this app ships `display: standalone` in its
 * manifest, an installed iPhone user hits this by default — so we tell
 * them to open the room in Safari instead of letting it fail silently.
 */
export function micBlocker(): { reason: string; hint: string } | null {
  if (!canCaptureMic()) {
    return {
      reason: 'This browser can\'t access a microphone.',
      hint: 'You can still join to listen and use chat.',
    };
  }
  if (isIOS() && isStandalonePWA()) {
    return {
      reason: 'iPhone home-screen apps can\'t use the microphone.',
      hint: 'Open bchnostr.com in Safari to speak — listening works here.',
    };
  }
  return null;
}

// ── Slugs ─────────────────────────────────────────────────────────

/**
 * Normalise arbitrary text into a room slug, used as the `d` tag of the
 * kind 30312 event. Restricted to `[a-z0-9-]` so a slug can be dropped
 * into a URL or an `a` coordinate without escaping concerns.
 */
export function slugifyRoom(input: string): string {
  return input
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
}

/** True if `slug` is a well-formed room identifier. */
export function isValidRoomSlug(slug: string): boolean {
  return /^[a-z0-9][a-z0-9-]{0,47}$/.test(slug);
}

/** The NIP-01 `a` coordinate for a room, used by chat and presence. */
export function roomCoordinate(hostPubkey: string, slug: string): string {
  return `${ROOM_KIND}:${hostPubkey}:${slug}`;
}

/** Parse an `a` coordinate back into its parts, or null if malformed. */
export function parseRoomCoordinate(
  coord: string,
): { host: string; slug: string } | null {
  const parts = coord.split(':');
  if (parts.length !== 3) return null;
  const [kind, host, slug] = parts;
  if (kind !== String(ROOM_KIND)) return null;
  if (!/^[0-9a-f]{64}$/.test(host)) return null;
  if (!isValidRoomSlug(slug)) return null;
  return { host, slug };
}

// ── Room parsing ──────────────────────────────────────────────────

export interface Room {
  /** `d` tag — unique per host. */
  slug: string;
  /** Full `a` coordinate. */
  coordinate: string;
  title: string;
  summary: string;
  image: string;
  /** Whoever published the kind 30312 event. */
  host: string;
  /** `status` tag: open rooms are joinable, closed ones are not. */
  status: 'open' | 'private' | 'closed';
  createdAt: number;
  eventId: string;
}

function tagValue(ev: Event, name: string): string | undefined {
  return ev.tags.find(t => t[0] === name)?.[1];
}

/**
 * Sanitise an event-sourced image URL. Room metadata is attacker-
 * controlled, so only https images are allowed through — never a
 * `javascript:` or `data:` URL that could end up in an `src`.
 */
function safeImage(raw: string | undefined): string {
  if (!raw) return '';
  try {
    const url = new URL(raw);
    return url.protocol === 'https:' ? url.href : '';
  } catch {
    return '';
  }
}

/** Parse a kind 30312 event into a Room, or null if unusable. */
export function parseRoom(ev: Event): Room | null {
  if (ev.kind !== ROOM_KIND) return null;

  const slug = tagValue(ev, 'd') || '';
  if (!isValidRoomSlug(slug)) return null;

  const rawStatus = tagValue(ev, 'status');
  const status: Room['status'] =
    rawStatus === 'private' ? 'private' : rawStatus === 'closed' ? 'closed' : 'open';

  return {
    slug,
    coordinate: roomCoordinate(ev.pubkey, slug),
    // NIP-53 uses `room` for the display name on 30312; accept `title` too
    // since that is what most clients (and our own composer) reach for.
    title: tagValue(ev, 'room') || tagValue(ev, 'title') || slug,
    summary: tagValue(ev, 'summary') || '',
    image: safeImage(tagValue(ev, 'image')),
    host: ev.pubkey,
    status,
    createdAt: ev.created_at,
    eventId: ev.id,
  };
}

/**
 * Collapse kind 30312 events into a de-duplicated room list, newest
 * event per host+slug winning (they are addressable, so older copies
 * from slower relays are stale).
 */
export function dedupeRooms(events: Event[]): Room[] {
  const byCoord = new Map<string, Room>();
  for (const ev of events) {
    const room = parseRoom(ev);
    if (!room) continue;
    const existing = byCoord.get(room.coordinate);
    if (!existing || room.createdAt > existing.createdAt) {
      byCoord.set(room.coordinate, room);
    }
  }
  return [...byCoord.values()].sort((a, b) => b.createdAt - a.createdAt);
}

// ── Presence ──────────────────────────────────────────────────────

export interface PresenceEntry {
  pubkey: string;
  /** Room `a` coordinate this user is present in. */
  coordinate: string;
  handRaised: boolean;
  /** created_at of the presence event, in unix seconds. */
  at: number;
}

/**
 * Parse a kind 10312 presence event.
 *
 * Presence is self-reported and only ever trusted for its *own* author:
 * `ev.pubkey` is the participant. A presence event can claim any room,
 * which is fine — claiming to be in a room is what joining *is* — but it
 * can never say anything about another user.
 */
export function parsePresence(ev: Event): PresenceEntry | null {
  if (ev.kind !== ROOM_PRESENCE_KIND) return null;
  const coord = ev.tags.find(t => t[0] === 'a')?.[1];
  if (!coord || !parseRoomCoordinate(coord)) return null;
  return {
    pubkey: ev.pubkey,
    coordinate: coord,
    handRaised: tagValue(ev, 'hand') === '1',
    at: ev.created_at,
  };
}

/**
 * Reduce presence events to the users currently in a room.
 *
 * Keeps the newest event per pubkey and drops anything older than
 * `PRESENCE_TIMEOUT_MS`, which is what prevents a roster full of ghosts.
 */
export function activePresence(
  events: Event[],
  coordinate: string,
  nowMs: number = Date.now(),
): PresenceEntry[] {
  const cutoff = nowMs / 1000 - PRESENCE_TIMEOUT_MS / 1000;
  const byPubkey = new Map<string, PresenceEntry>();

  for (const ev of events) {
    const entry = parsePresence(ev);
    if (!entry) continue;
    if (entry.coordinate !== coordinate) continue;
    if (entry.at < cutoff) continue;
    const existing = byPubkey.get(entry.pubkey);
    if (!existing || entry.at > existing.at) byPubkey.set(entry.pubkey, entry);
  }

  return [...byPubkey.values()].sort((a, b) => a.at - b.at);
}

/** Count of live participants per room coordinate, for the directory. */
export function presenceCounts(
  events: Event[],
  nowMs: number = Date.now(),
): Map<string, number> {
  const cutoff = nowMs / 1000 - PRESENCE_TIMEOUT_MS / 1000;
  const newest = new Map<string, PresenceEntry>();

  for (const ev of events) {
    const entry = parsePresence(ev);
    if (!entry || entry.at < cutoff) continue;
    const existing = newest.get(entry.pubkey);
    if (!existing || entry.at > existing.at) newest.set(entry.pubkey, entry);
  }

  const counts = new Map<string, number>();
  for (const entry of newest.values()) {
    counts.set(entry.coordinate, (counts.get(entry.coordinate) || 0) + 1);
  }
  return counts;
}
